import type { ReactNode } from "react";
import type { SvgIconComponent } from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import { cn } from "@/utils/cn";
import { Breadcrumbs, type BreadcrumbItem } from "./Breadcrumbs";
import { PageTitleIcon } from "./PageTitleIcon";
import { resolveNavIconForPath } from "@/utils/pageNavIcon";
import { useActiveModule } from "@/hooks/useActiveModule";
import { getModuleAccentIconClass } from "@/config/appAccent";

interface PageHeaderProps {
  title: string;
  description?: ReactNode;
  breadcrumbs?: BreadcrumbItem[];
  actions?: ReactNode;
  /** Override auto-resolved page icon from current route. Pass `null` to hide. */
  icon?: SvgIconComponent | null;
  /** When true (default), show the nav icon for the current pathname. */
  showIcon?: boolean;
  className?: string;
}

export function PageHeader({
  title,
  description: _description,
  breadcrumbs,
  actions,
  icon,
  showIcon = true,
  className,
}: PageHeaderProps) {
  const location = useLocation();
  const activeModule = useActiveModule();
  const resolvedIcon =
    icon !== undefined
      ? icon
      : showIcon
        ? resolveNavIconForPath(location.pathname)
        : null;
  const titleIconClass =
    activeModule && resolvedIcon
      ? getModuleAccentIconClass(activeModule)
      : undefined;

  return (
    <header className={cn("mb-6", className)}>
      {breadcrumbs && breadcrumbs.length > 0 && (
        <div className="mb-2">
          <Breadcrumbs items={breadcrumbs} />
        </div>
      )}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="flex items-center gap-2.5 text-xl font-semibold tracking-tight text-slate-900 dark:text-white">
            {resolvedIcon ? (
              <PageTitleIcon icon={resolvedIcon} className={titleIconClass} />
            ) : null}
            <span className="min-w-0 truncate">{title}</span>
          </h1>
        </div>
        {actions && (
          <div className="flex flex-wrap items-center gap-2">{actions}</div>
        )}
      </div>
    </header>
  );
}
